<template>
  <quota-menu-button
    :read-only="readOnly || disabled"
    :is-alert-on="isAlertOn"
    :is-input-invalid="isInputInvalid"
    v-model:is-open="isOpen"
    hide-slider
    :button-class="buttonClass"
    :tooltip-alert-class="tooltipAlertClass"
    @reset-value="resetValue"
    @update:model-value="updateModelValue"
    :aid="`limit-button-${model.resource.nodePool?.name}`"
  >
    <template #editable-text>{{ editableText }}</template>
    <template #resource-label>
      {{ entityText }} max.<br />
      GPU device allocation</template
    >
    <template #input>
      <runai-numeric-input
        class="q-mr-md"
        aid="limit-quota-input"
        ref="numeric-input"
        autofocus
        v-model.number="inputValue"
        :placeholder="EResourceState.Unlimited"
        no-error-icon
        stack-label
        :read-only="readOnly"
      >
        <template v-if="isInputInvalid" #error>
          <div v-text="alertText" class="limit-exceeds-alert" :class="textAlertClass"></div>
        </template>
      </runai-numeric-input>
    </template>
    <template #alert-text>
      {{ alertText }}
    </template>
  </quota-menu-button>
</template>

<script lang="ts">
import { defineComponent, inject, type PropType, ref, toRef } from "vue";

// cmps
import { RunaiNumericInput } from "@/components/common/runai-numeric-input";

// models
import {
  EQuotaEntity,
  EResourceState,
  RESOURCE_MAX_ALLOWED_INFINITE_VALUE,
  RESOURCE_MAX_ALLOWED_INFINITE_VALUE_NUMERIC,
} from "@/models/resource.model";
//utils
//store
import {
  useQuotaInput,
  type useQuotaInputProperties,
} from "@/components/org-unit/quota-management/quota-inputs/quota-input-composable/use-quota-input.composeable";
import { QuotaMenuButton } from "@/components/org-unit/quota-management/quota-inputs/quota-menu-button";
import type { NodePoolModel } from "@/models/org-unit.model";
import { orgUnitUtil } from "@/utils/org-unit.util";

export default defineComponent({
  name: "limit-input",
  components: {
    QuotaMenuButton,
    RunaiNumericInput,
  },
  emits: ["update:model-value", "close"],
  inject: ["entity"],
  props: {
    model: {
      type: Object as PropType<NodePoolModel>,
      required: true,
    },
    modelValue: {
      type: [Number, null] as PropType<number | null>,
      default: null,
    },
    readOnly: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    isAlertOn: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  data() {
    return {
      inputValue: this.modelValue as number | null | undefined,
      disabled: false as boolean,
    };
  },
  setup(props) {
    const entity = ref(inject<EQuotaEntity>("entity"));
    if (!entity.value) {
      throw new Error("'entity' is not provided");
    }

    const {
      isOpen,
      isOverQuotaPriorityEnabled,
      buttonClass,
      textAlertClass,
      tooltipAlertClass,
    }: useQuotaInputProperties = useQuotaInput(entity.value, toRef(props, "isAlertOn"), true);

    return {
      isOpen,
      isOverQuotaPriorityEnabled,
      buttonClass,
      textAlertClass,
      tooltipAlertClass,
    };
  },
  created() {
    this.disabled = !this.isOverQuotaEnabled;
    if (this.modelValue === RESOURCE_MAX_ALLOWED_INFINITE_VALUE_NUMERIC) {
      this.inputValue = RESOURCE_MAX_ALLOWED_INFINITE_VALUE;
    }
  },
  computed: {
    EQuotaEntity(): typeof EQuotaEntity {
      return EQuotaEntity;
    },
    EResourceState(): typeof EResourceState {
      return EResourceState;
    },
    alertText(): string {
      return `The ${this.entity}’s maximum GPU device allocation is below its quota. To stay above the quota, enter a number above ${this.model.resource.gpu.deserved} GPUs.`;
    },
    isOverQuotaEnabled(): boolean {
      return orgUnitUtil.isResourceOverQuotaEnabled(this.model.resource, this.isOverQuotaPriorityEnabled);
    },
    editableText() {
      if (
        this.modelValue === RESOURCE_MAX_ALLOWED_INFINITE_VALUE ||
        this.modelValue === RESOURCE_MAX_ALLOWED_INFINITE_VALUE_NUMERIC
      )
        return EResourceState.Unlimited;
      return this.modelValue.toFixed(2);
    },
    isInputInvalid(): boolean {
      return !orgUnitUtil.isOverQuotaValueValid(this.inputValue, this.model.resource.gpu, this.isOverQuotaEnabled);
    },
    entityText(): string {
      return this.entity === EQuotaEntity.project ? "Project" : "Department";
    },
  },
  methods: {
    resetValue(): void {
      this.inputValue = this.modelValue;
    },
    updateModelValue(): void {
      if (this.isInputInvalid) return;
      this.$emit("update:model-value", this.inputValue);
    },
  },
  watch: {
    isOverQuotaEnabled(newValue: boolean) {
      if (!newValue) {
        this.inputValue = this.model.resource.gpu.deserved;
        this.disabled = true;
      } else {
        this.inputValue = RESOURCE_MAX_ALLOWED_INFINITE_VALUE;
        this.disabled = false;
      }
      this.$emit("update:model-value", this.inputValue);
    },
  },
});
</script>
<style lang="scss" scoped>
.limit-exceeds-alert {
  max-width: 200px;
}
</style>
